import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/common/Toast';
import { SEO } from '../../components/common/SEO';
import { getInvoicesDB, saveInvoiceDB, saveMemberDB, saveTransactionDB, getGelanggangDB } from '../../services/db';
import { verifyToyyibPayBillStatus } from '../../lib/toyyibpay';
import { calculateOnboardingFees, createOnboardingInvoice, UNIFORM_SIZES, getUniformPriceBySize } from '../../utils/pricing';
import { formatPublicName, sanitizeMemberId } from '../../utils/formatters';
import { InvoiceItem, GelanggangDetail, UserProfile } from '../../types';
import { uploadYuranReceiptToAhliDriveJson } from '../../lib/gdrive';
import { UniformSizeChartModal } from '../../components/common/UniformSizeChartModal';
import {
  CreditCard,
  ShieldCheck,
  CheckCircle2,
  Clock,
  Sparkles,
  QrCode,
  LogOut,
  Upload,
  RefreshCw,
  Info,
  Building2,
  Phone,
  Calendar,
  AlertCircle,
  FileText,
  UserCheck,
  Copy,
  Check,
  Ruler
} from 'lucide-react';

interface OnboardingPaymentPageProps {
  onPaymentCompleted?: () => void;
}

export const OnboardingPaymentPage: React.FC<OnboardingPaymentPageProps> = ({ onPaymentCompleted }) => {
  const { currentUser, setCurrentUser, logout } = useAuth();
  const { showToast } = useToast();

  const [invoice, setInvoice] = useState<InvoiceItem | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  const [isUploadingReceipt, setIsUploadingReceipt] = useState<boolean>(false);
  const [gelanggangInfo, setGelanggangInfo] = useState<GelanggangDetail | null>(null);
  const [selectedSize, setSelectedSize] = useState<string>(currentUser?.uniformSize || '150cm');
  const [isSizeChartOpen, setIsSizeChartOpen] = useState<boolean>(false);
  const [isAccountCopied, setIsAccountCopied] = useState<boolean>(false);
  const [isRefCopied, setIsRefCopied] = useState<boolean>(false);
  const hasInitializedRef = useRef<boolean>(false);

  const accountNo = gelanggangInfo?.qrAccountNo || '02093010041234';
  const paymentRef = `ONBOARDING-${sanitizeMemberId(currentUser?.id)}`;

  const handleCopyAccountNo = async () => {
    try {
      await navigator.clipboard.writeText(accountNo.replace(/[^0-9]/g, ''));
      setIsAccountCopied(true);
      showToast('success', 'No. Akaun Disalin!', `${accountNo} berjaya disalin ke papan klip.`);
      setTimeout(() => setIsAccountCopied(false), 2500);
    } catch {
      showToast('error', 'Ralat', 'Gagal menyalin nombor akaun.');
    }
  };

  const handleCopyPaymentRef = async () => {
    try {
      await navigator.clipboard.writeText(paymentRef);
      setIsRefCopied(true);
      showToast('success', 'Rujukan Bayaran Disalin!', `${paymentRef} berjaya disalin ke papan klip.`);
      setTimeout(() => setIsRefCopied(false), 2500);
    } catch {
      showToast('error', 'Ralat', 'Gagal menyalin rujukan bayaran.');
    }
  };

  const fees = calculateOnboardingFees(currentUser?.icNo, undefined, selectedSize);

  const handleSizeChange = async (newSize: string) => {
    setSelectedSize(newSize);
    hasInitializedRef.current = true;
    if (!currentUser) return;

    const newFees = calculateOnboardingFees(currentUser.icNo, undefined, newSize);

    // Save updated size to user profile immediately in state
    const updatedMember: UserProfile = {
      ...currentUser,
      uniformSize: newSize
    };
    setCurrentUser(updatedMember);

    // Update invoice immediately in state
    const updatedInvoice: InvoiceItem = invoice ? {
      ...invoice,
      amount: newFees.total,
      description: `Pakej kemasukan pesilat baharu: Uniform rasmi saiz ${newSize} (RM${newFees.uniformFee}), Yuran tahunan ${newFees.year} (RM${newFees.yearlyFee}), dan Yuran latihan bulanan pertama (RM${newFees.monthlyFee}).`,
      items: invoice.items?.map(it => {
        if (it.id === 'item-uniform') {
          return {
            ...it,
            description: `Pakej Uniform Rasmi Lengkap ASMM (Saiz: ${newSize} - Baju, Seluar & Bengkung Putih Asas)`,
            unitPrice: newFees.uniformFee,
            amount: newFees.uniformFee
          };
        }
        return it;
      }) || []
    } : createOnboardingInvoice(updatedMember, newSize);

    setInvoice(updatedInvoice);

    // Persist changes to DB
    try {
      await Promise.all([
        saveMemberDB(updatedMember),
        saveInvoiceDB(updatedInvoice)
      ]);
    } catch (err) {
      console.error('Error saving updated uniform size:', err);
    }

    showToast('info', 'Saiz Uniform Dipilih', `Saiz ${newSize} dipilih (RM ${newFees.uniformFee}). Jumlah bayaran dikemaskini ke RM ${newFees.total.toFixed(2)}.`);
  };

  // Initialize or fetch onboarding invoice
  useEffect(() => {
    let isMounted = true;
    if (!currentUser?.id) return;

    async function initInvoice() {
      setIsLoading(true);

      try {
        const [allInvoices, allGelanggang] = await Promise.all([
          getInvoicesDB(),
          getGelanggangDB()
        ]);

        if (currentUser?.gelanggang) {
          const matchGel = allGelanggang.find(g => g.name === currentUser.gelanggang);
          if (matchGel && isMounted) setGelanggangInfo(matchGel);
        }

        // Search for existing onboarding invoice
        let existing = allInvoices.find(inv =>
          (inv.memberId === currentUser?.id || inv.id === currentUser?.onboardingInvoiceId) &&
          (inv.category === 'Yuran Pendaftaran' || (inv.title && inv.title.toLowerCase().includes('pendaftaran ahli baharu')))
        );

        // If not found, create and save a new onboarding invoice
        if (!existing && currentUser) {
          const newInv = createOnboardingInvoice(currentUser, currentUser.uniformSize || selectedSize);
          await saveInvoiceDB(newInv);
          existing = newInv;

          // Link invoice ID to user profile
          if (currentUser.onboardingInvoiceId !== newInv.id) {
            const updatedUser = {
              ...currentUser,
              onboardingInvoiceId: newInv.id,
              onboardingStatus: 'pending_payment' as const
            };
            await saveMemberDB(updatedUser);
            setCurrentUser(updatedUser);
          }
        }

        if (existing?.items && !hasInitializedRef.current) {
          const uItem = existing.items.find(it => it.id === 'item-uniform');
          if (uItem?.description) {
            const matchSize = uItem.description.match(/(\d{3}cm)/);
            if (matchSize && matchSize[1] && isMounted) {
              setSelectedSize(matchSize[1]);
            }
          }
          hasInitializedRef.current = true;
        }

        if (isMounted) {
          setInvoice(existing || null);
        }
      } catch (err) {
        console.error('Error initializing onboarding invoice:', err);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    }

    initInvoice();
    return () => { isMounted = false; };
  }, [currentUser?.id]);



  // Handle Verify ToyyibPay Payment Status
  const handleVerifyStatus = async () => {
    if (!invoice || !currentUser) return;
    setIsVerifying(true);

    try {
      if (invoice.toyibBillCode) {
        const verifyRes = await verifyToyyibPayBillStatus(invoice.toyibBillCode);
        if (verifyRes.status === 'Disahkan') {
          await completeOnboardingPayment('ToyyibPay FPX');
          return;
        }
      }

      // Check current invoice from db
      const allInvoices = await getInvoicesDB();
      const currentInv = allInvoices.find(i => i.id === invoice.id);
      if (currentInv && currentInv.status === 'Lulus / Dibayar') {
        await completeOnboardingPayment(currentInv.paymentMethod || 'FPX');
        return;
      }

      showToast('info', 'Status Belum Disahkan', 'Pembayaran belum dikesan selesai. Sila pastikan transaksi FPX anda telah berjaya atau muat naik resit pindahan.');
    } catch {
      showToast('error', 'Ralat Semakan', 'Gagal menyemak status transaksi.');
    } finally {
      setIsVerifying(false);
    }
  };

  // Upload receipt for DuitNow QR / Manual Transfer (Strictly PDF only)
  const handleUploadReceipt = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !invoice || !currentUser) return;

    // Strict validation: Only PDF files allowed
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      showToast('error', 'Format Tidak Sah', 'Sila muat naik fail resit dalam format dokumen PDF (.pdf) sahaja.');
      if (e.target) e.target.value = '';
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      showToast('error', 'Saiz Melebihi Had (10MB)', 'Saiz fail dokumen PDF resit tidak boleh melebihi 10MB.');
      if (e.target) e.target.value = '';
      return;
    }

    setIsUploadingReceipt(true);
    try {
      const curYear = new Date().getFullYear();
      const monthNames = [
        'Januari', 'Februari', 'Mac', 'April', 'Mei', 'Jun',
        'Julai', 'Ogos', 'September', 'Oktober', 'November', 'Disember'
      ];
      const curMonth = monthNames[new Date().getMonth()];

      const res = await uploadYuranReceiptToAhliDriveJson(
        file,
        currentUser.id,
        currentUser.name,
        `Onboarding_${curMonth}`,
        curYear,
        currentUser.icNo
      );

      const now = new Date().toISOString();

      const updatedInvoice: InvoiceItem = {
        ...invoice,
        status: 'Menunggu Pengesahan',
        receiptUrl: res.fileUrl,
        receiptFileName: res.fileName,
        paidAt: now.split('T')[0],
        paymentMethod: 'DuitNow QR'
      };

      await saveInvoiceDB(updatedInvoice);
      setInvoice(updatedInvoice);

      // Record financial transaction for audit
      await saveTransactionDB({
        id: `tx-onb-${Date.now()}`,
        date: now.split('T')[0],
        category: 'Yuran Pendaftaran',
        description: `Pakej Pendaftaran Ahli Baharu (Uniform, Tahunan, Bulanan) - ${currentUser.name}`,
        amount: invoice.amount,
        type: 'Income',
        payerOrPayee: currentUser.name,
        gelanggang: currentUser.gelanggang,
        recordedBy: 'Sistem Portal (Onboarding)',
        status: 'Menunggu Pengesahan',
        receiptUrl: res.fileUrl
      });

      showToast('success', 'Resit PDF Berjaya Dimuat Naik!', 'Resit pembayaran format PDF telah diterima. Pengesahan akan disemak oleh pengurusan persatuan.');
    } catch (err: any) {
      showToast('error', 'Gagal Muat Naik', err?.message || 'Sila pastikan fail resit PDF sah dan tidak melebihi 10MB.');
    } finally {
      setIsUploadingReceipt(false);
      if (e.target) e.target.value = '';
    }
  };

  // Settle & complete onboarding
  const completeOnboardingPayment = async (method: string) => {
    if (!currentUser || !invoice) return;

    const now = new Date().toISOString();
    const updatedInvoice: InvoiceItem = {
      ...invoice,
      status: 'Lulus / Dibayar',
      paidAt: now.split('T')[0],
      paymentMethod: method
    };
    await saveInvoiceDB(updatedInvoice);
    setInvoice(updatedInvoice);

    // Save income transaction
    await saveTransactionDB({
      id: `tx-onb-paid-${Date.now()}`,
      date: now.split('T')[0],
      category: 'Yuran Pendaftaran',
      description: `Bayaran Pakej Onboarding Ahli Baharu Disahkan - ${currentUser.name}`,
      amount: invoice.amount,
      type: 'Income',
      payerOrPayee: currentUser.name,
      gelanggang: currentUser.gelanggang,
      recordedBy: 'Sistem FPX ToyyibPay',
      status: 'Disahkan'
    });

    // Update user profile to completed
    const updatedUser = {
      ...currentUser,
      status: 'Aktif' as const,
      onboardingStatus: 'completed' as const
    };
    await saveMemberDB(updatedUser);
    setCurrentUser(updatedUser);
    window.dispatchEvent(new Event('asmm_db_updated'));

    showToast('success', 'Onboarding Selesai!', 'Pembayaran telah disahkan. Selamat datang ke Dashboard Ahli PSSASMM.');
    if (onPaymentCompleted) {
      onPaymentCompleted();
    }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 flex flex-col justify-between py-6 sm:py-10 px-4 sm:px-6 lg:px-8">
      <SEO
        title="Pakej Onboarding & Yuran Keahlian | PSSASMM"
        description="Lengkapkan bayaran pakej uniform rasmi, yuran tahunan, dan yuran latihan pertama untuk mula menggunakan dashboard pesilat."
      />

      <div className="max-w-3xl w-full mx-auto space-y-6 animate-fade-in">
        {/* Top Header Card */}
        <header className="bg-slate-950 text-white rounded-3xl p-6 sm:p-8 shadow-xl relative overflow-hidden border border-slate-800">
          <div className="absolute right-0 top-0 translate-x-12 -translate-y-12 w-48 h-48 bg-asmm-red/30 rounded-full blur-3xl pointer-events-none" />

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-5 relative z-10">
            <div className="flex items-center gap-4">
              <img
                src={currentUser?.photoUrl || '/default-avatar.svg'}
                alt={currentUser?.name}
                onError={(e) => { (e.target as HTMLElement).setAttribute('src', '/default-avatar.svg'); }}
                className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl object-cover border-2 border-white/20 shadow-md shrink-0"
              />
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider bg-amber-400 text-slate-950 rounded-md">
                    Langkah Terakhir Onboarding
                  </span>
                  <span className="text-xs text-slate-300 font-mono">
                    ID: {sanitizeMemberId(currentUser?.id)}
                  </span>
                </div>
                <h1 className="text-xl sm:text-2xl font-black text-white leading-tight">
                  Pengaktifan Keahlian Pesilat
                </h1>
                <p className="text-xs text-slate-300 mt-1">
                  Selesaikan bayaran pakej onboarding untuk membuka akses penuh ke Dashboard.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={logout}
              className="px-3.5 py-2 bg-white/10 hover:bg-rose-600/30 text-slate-200 hover:text-white border border-white/10 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 self-start sm:self-center cursor-pointer shrink-0"
              title="Log keluar dan tukar akaun"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span>Log Keluar</span>
            </button>
          </div>
        </header>

        {/* Member Category & Age Breakdown Notice */}
        <div className="bg-slate-50 border border-slate-200 rounded-3xl p-5 sm:p-6 space-y-4">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">
                Pesilat Berdaftar
              </span>
              <h2 className="text-lg sm:text-xl font-extrabold text-slate-900 mt-0.5">
                {formatPublicName(currentUser?.name || 'Pesilat ASMM')}
              </h2>
              <div className="flex items-center gap-2 text-xs text-slate-600 mt-1 flex-wrap">
                <span>No. Kad Pengenalan: <strong className="text-slate-900 font-mono">{currentUser?.icNo || '-'}</strong></span>
                <span>•</span>
                <span>Kategori: <strong className="text-asmm-red font-bold">{fees.ageLabel}{fees.age > 0 ? ` (${fees.age} Tahun)` : ''}</strong></span>
                {currentUser?.gelanggang && (
                  <>
                    <span>•</span>
                    <span>Gelanggang: <strong className="text-slate-900">{currentUser.gelanggang}</strong></span>
                  </>
                )}
              </div>
            </div>

            <div className="px-3.5 py-2 bg-white rounded-2xl border border-slate-200 text-right shadow-2xs">
              <span className="text-[10px] font-bold text-slate-400 block uppercase">Jumlah Onboarding</span>
              <span className="text-xl sm:text-2xl font-black text-asmm-red">
                RM {fees.total.toFixed(2)}
              </span>
            </div>
          </div>

          {/* Itemized Fee Breakdown Card */}
          <div className="bg-white rounded-2xl border border-slate-200/90 divide-y divide-slate-100 overflow-hidden shadow-2xs">
            {/* Item 1: Uniform Package with Size Selection */}
            <div className="p-4 sm:p-4.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-start gap-3">
                <div className="p-2.5 bg-red-50 text-asmm-red rounded-xl shrink-0 mt-0.5">
                  <ShieldCheck className="w-5 h-5" />
                </div>
                <div className="space-y-2">
                  <div>
                    <h3 className="text-xs sm:text-sm font-extrabold text-slate-900">
                      Pakej Uniform Rasmi ASMM
                    </h3>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      Termasuk Baju Silat, Seluar Silat tahan lasak, dan Bengkung Putih Rasmi PSSASMM.
                    </p>
                  </div>

                  {/* Size Selector */}
                  <div className="flex items-center gap-2 flex-wrap pt-0.5">
                    <label className="text-xs font-bold text-slate-700">Pilih Saiz Uniform:</label>
                    <select
                      value={selectedSize}
                      onChange={(e) => handleSizeChange(e.target.value)}
                      className="px-3 py-1.5 rounded-xl border border-slate-300 bg-white text-xs font-bold text-slate-900 focus:border-asmm-red focus:outline-none cursor-pointer shadow-3xs"
                    >
                      {UNIFORM_SIZES.map(s => (
                        <option key={s} value={s}>
                          {s} (RM {getUniformPriceBySize(s)})
                        </option>
                      ))}
                    </select>

                    <button
                      type="button"
                      onClick={() => setIsSizeChartOpen(true)}
                      className="text-xs font-bold text-blue-600 hover:text-blue-800 underline inline-flex items-center gap-1 cursor-pointer bg-transparent border-none p-0 ml-1"
                      title="Buka Carta Saiz Uniform"
                    >
                      <Ruler className="w-3.5 h-3.5" />
                      <span>Carta Saiz</span>
                    </button>


                  </div>
                </div>
              </div>
              <div className="text-right shrink-0">
                <span className="text-xs sm:text-sm font-extrabold text-slate-900">
                  RM {fees.uniformFee.toFixed(2)}
                </span>
                <span className="block text-[10px] text-slate-400 font-medium">Saiz {selectedSize}</span>
              </div>
            </div>

            {/* Item 2: Yearly Fee */}
            <div className="p-4 sm:p-4.5 flex items-center justify-between gap-3">
              <div className="flex items-start gap-3">
                <div className="p-2.5 bg-amber-50 text-amber-600 rounded-xl shrink-0 mt-0.5">
                  <Calendar className="w-5 h-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-xs sm:text-sm font-extrabold text-slate-900">
                      Yuran Keahlian Tahunan ASMM ({fees.year})
                    </h3>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    Keahlian pertubuhan tahunan (RM10 semasa, RM20 bermula tahun 2027).
                  </p>
                </div>
              </div>
              <div className="text-right shrink-0">
                <span className="text-xs sm:text-sm font-extrabold text-slate-900">
                  RM {fees.yearlyFee.toFixed(2)}
                </span>
                <span className="block text-[10px] text-slate-400 font-medium">Setahun ({fees.year})</span>
              </div>
            </div>

            {/* Item 3: First Monthly Fee */}
            <div className="p-4 sm:p-4.5 flex items-center justify-between gap-3">
              <div className="flex items-start gap-3">
                <div className="p-2.5 bg-blue-50 text-blue-600 rounded-xl shrink-0 mt-0.5">
                  <Clock className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-xs sm:text-sm font-extrabold text-slate-900">
                    Yuran Bulanan Latihan Pertama ({fees.ageLabel})
                  </h3>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    Sesi latihan di gelanggang pilihan (RM30 untuk 18 tahun ke atas, RM20 bawah 18 tahun).
                  </p>
                </div>
              </div>
              <div className="text-right shrink-0">
                <span className="text-xs sm:text-sm font-extrabold text-slate-900">
                  RM {fees.monthlyFee.toFixed(2)}
                </span>
                <span className="block text-[10px] text-slate-400 font-medium">Bulan Pertama</span>
              </div>
            </div>
          </div>
        </div>

        {/* Current Invoice Status Alert Banner */}
        {invoice?.status === 'Menunggu Pengesahan' && (
          <div className="p-4 bg-amber-50 border border-amber-200/90 rounded-2xl flex items-center justify-between gap-3 text-amber-900 shadow-xs">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-amber-600 shrink-0 animate-pulse" />
              <div>
                <h4 className="text-xs sm:text-sm font-extrabold text-amber-950">
                  Pembayaran Sedang Disahkan
                </h4>
                <p className="text-[11px] text-amber-800 mt-0.5">
                  Resit transaksi bayaran anda telah dihantar. Klik butang semak status di bawah untuk kemaskini.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={handleVerifyStatus}
              disabled={isVerifying}
              className="px-3.5 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shrink-0 cursor-pointer disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${isVerifying ? 'animate-spin' : ''}`} />
              <span>Semak Status</span>
            </button>
          </div>
        )}

        {/* Payment Action: DuitNow QR & Pindahan Bank (QR Only) */}
        <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-7 shadow-sm space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-slate-900 text-white flex items-center justify-center shadow-2xs shrink-0">
                <QrCode className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-extrabold text-slate-900">
                  DuitNow QR & Pindahan Bank
                </h3>
                <p className="text-xs text-slate-500">
                  Imbas kod QR rasmi gelanggang atau buat pindahan manual ke akaun bank di bawah.
                </p>
              </div>
            </div>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-bold rounded-full self-start sm:self-center">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
              <span>Saluran Rasmi Gelanggang</span>
            </span>
          </div>

          <div className="flex flex-col md:flex-row items-center gap-6 bg-slate-50/80 p-5 sm:p-6 rounded-2xl border border-slate-200">
            <div className="flex flex-col items-center gap-2 shrink-0">
              <img
                src={gelanggangInfo?.qrImageUrl || '/logo-pssasmm.png'}
                alt="QR Payment"
                className="w-40 h-40 object-contain rounded-2xl border border-slate-200 p-2.5 bg-white shadow-2xs"
                onError={(e) => { (e.target as HTMLElement).setAttribute('src', '/logo-pssasmm.png'); }}
              />
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                DuitNow QR
              </span>
            </div>

            <div className="space-y-3 text-xs text-slate-700 w-full">
              <div>
                <span className="text-slate-400 font-medium block text-[11px]">Penerima:</span>
                <strong className="text-slate-900 font-bold text-sm">
                  {gelanggangInfo?.qrAccountName || 'PERSATUAN SENI SILAT AWANG SULUNG MERAH MUDA'}
                </strong>
              </div>

              <div>
                <span className="text-slate-400 font-medium block text-[11px]">Bank:</span>
                <strong className="text-slate-900 font-bold">
                  {gelanggangInfo?.qrBankName || 'Bank Islam Malaysia Berhad (BIMB)'}
                </strong>
              </div>

              {/* No. Akaun (One-Click Copiable) */}
              <div className="pt-0.5">
                <span className="text-slate-500 font-bold block text-[11px] mb-1">No. Akaun:</span>
                <button
                  type="button"
                  onClick={handleCopyAccountNo}
                  title="Klik satu kali untuk salin No. Akaun"
                  className="group inline-flex items-center gap-2.5 px-3.5 py-2 bg-white hover:bg-red-50 border border-slate-200 hover:border-asmm-red/50 rounded-xl transition-all cursor-pointer shadow-3xs active:scale-[0.98]"
                >
                  <strong className="text-asmm-red font-mono text-sm sm:text-base tracking-wide">
                    {accountNo}
                  </strong>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-100 group-hover:bg-red-100 text-[10px] font-bold text-slate-700 group-hover:text-asmm-red transition-colors">
                    {isAccountCopied ? (
                      <>
                        <Check className="w-3 h-3 text-emerald-600" />
                        <span className="text-emerald-700 font-bold">Disalin</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3 text-slate-500 group-hover:text-asmm-red" />
                        <span>Salin</span>
                      </>
                    )}
                  </span>
                </button>
              </div>

              {/* Rujukan Bayaran (One-Click Copiable) */}
              <div className="pt-0.5">
                <span className="text-slate-500 font-bold block text-[11px] mb-1">Rujukan Bayaran:</span>
                <button
                  type="button"
                  onClick={handleCopyPaymentRef}
                  title="Klik satu kali untuk salin Rujukan Bayaran"
                  className="group inline-flex items-center gap-2.5 px-3.5 py-2 bg-white hover:bg-blue-50 border border-slate-200 hover:border-blue-400 rounded-xl transition-all cursor-pointer shadow-3xs active:scale-[0.98]"
                >
                  <strong className="font-mono text-slate-800 text-xs sm:text-sm tracking-wide group-hover:text-blue-900">
                    {paymentRef}
                  </strong>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-100 group-hover:bg-blue-100 text-[10px] font-bold text-slate-700 group-hover:text-blue-700 transition-colors">
                    {isRefCopied ? (
                      <>
                        <Check className="w-3 h-3 text-emerald-600" />
                        <span className="text-emerald-700 font-bold">Disalin</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3 text-slate-500 group-hover:text-blue-600" />
                        <span>Salin</span>
                      </>
                    )}
                  </span>
                </button>
              </div>
            </div>
          </div>

          {/* Upload Receipt CTA Button */}
          <div className="pt-1">
            <label className="w-full py-4 px-6 bg-slate-900 hover:bg-slate-800 text-white rounded-2xl text-sm font-black transition-all shadow-md flex items-center justify-center gap-2.5 cursor-pointer active:scale-[0.99] border-none">
              <Upload className="w-5 h-5 text-amber-400" />
              <span>{isUploadingReceipt ? 'Memuat Naik Resit PDF...' : 'Muat Naik Resit Bayaran (.PDF Sahaja)'}</span>
              <input
                type="file"
                accept="application/pdf,.pdf"
                onChange={handleUploadReceipt}
                disabled={isUploadingReceipt}
                className="sr-only"
              />
            </label>
            <p className="text-[11px] text-slate-400 text-center mt-2">
              Selepas membuat bayaran, sila muat naik slip resit transaksi dalam format dokumen <strong>PDF (.pdf)</strong> sahaja untuk semakan dan kelulusan akaun.
            </p>
          </div>
        </div>

        {/* Old Member Reassurance Footnote */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4 flex items-start gap-3 text-xs text-slate-600 shadow-2xs">
          <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <span className="font-bold text-slate-900 block">
              Pernah mendaftar atau pesilat lama sebelum sistem ini wujud?
            </span>
            <p className="text-[11px] text-slate-500 leading-relaxed">
              Jika anda merupakan ahli lama yang telah mempunyai uniform dan menyelesaikan onboarding terdahulu, sila maklumkan kepada <strong>Ketua Gelanggang</strong> atau <strong>Setiausaha ASMM</strong> untuk pengesahan pendaftaran manual ahli lama tanpa caj onboarding baharu.
            </p>
          </div>
        </div>
      </div>

      {/* Uniform Size Chart Modal Popup */}
      <UniformSizeChartModal
        isOpen={isSizeChartOpen}
        onClose={() => setIsSizeChartOpen(false)}
        selectedSize={selectedSize}
        onSelectSize={handleSizeChange}
      />
    </div>
  );
};
